import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";
import { useContent } from "../contexts/ContentContext";
import { resolveImage } from "../lib/image";

export const Header = () => {
  const { t, lang, setLang } = useI18n();
  const { content } = useContent();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/business", label: t.nav.business },
    { to: "/travel", label: t.nav.travel },
    { to: "/retreat", label: t.nav.retreat },
    { to: "/filming", label: t.nav.filming },
    { to: "/contacts", label: t.nav.contacts },
  ];

  const branding = content.branding || {};

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 h-[80px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <div
            className={`w-10 h-10 flex items-center justify-center overflow-hidden ${
              branding.logo_url ? "bg-transparent" : "bg-coral"
            }`}
          >
            {branding.logo_url && (
              <img
                src={resolveImage(branding.logo_url)}
                alt={branding.company_name || "Diyatour"}
                className="w-7 h-7 object-contain"
              />
            )}
          </div>
          <div className="leading-none">
            <div className="font-logo font-extrabold tracking-tight text-white text-[17px]">
              {branding.company_name || "DIYATOUR"}
            </div>
            <div className="font-menu text-[9px] tracking-[0.25em] text-white/40 mt-0.5 uppercase">
              {branding.tagline || "CHINA · BUSINESS · TECH"}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `font-menu text-[12px] font-bold tracking-[0.18em] uppercase transition-colors ${
                  isActive ? "text-white nav-active" : "text-white/60 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-1">
            {["ru", "en"].map((code) => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                onClick={() => setLang(code)}
                className={`px-2 py-1 text-[11px] font-mono tracking-widest uppercase transition-colors ${
                  lang === code ? "text-coral" : "text-white/50 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          <button
            className="hidden md:inline-flex p-2 text-white/60 hover:text-coral transition-colors"
            aria-label="search"
            data-testid="header-search-btn"
          >
            <Search size={18} />
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 -mr-2 text-white"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-midnight border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `font-menu text-[20px] font-bold uppercase tracking-tight ${
                      isActive ? "text-coral" : "text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-2 mt-3">
                {["ru", "en"].map((code) => (
                  <button
                    key={code}
                    data-testid={`mobile-lang-${code}`}
                    onClick={() => setLang(code)}
                    className={`px-3 py-2 text-[12px] font-mono tracking-widest uppercase border ${
                      lang === code
                        ? "bg-coral text-midnight border-coral"
                        : "border-white/20 text-white"
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>
              <Link to="/contacts" className="btn-primary justify-center mt-2" data-testid="mobile-cta">
                {t.nav.cta}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => {
  const { t } = useI18n();
  const { content } = useContent();
  const branding = content.branding || {};
  const channels = content.channels || {};
  const links = [
    { to: "/", label: t.nav.home },
    { to: "/business", label: t.nav.business },
    { to: "/travel", label: t.nav.travel },
    { to: "/retreat", label: t.nav.retreat },
    { to: "/filming", label: t.nav.filming },
    { to: "/contacts", label: t.nav.contacts },
  ];
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0E1117] text-white relative overflow-hidden border-t border-white/5"
    >
      <div className="absolute inset-0 tech-grid-bg opacity-50" />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <div
              className={`w-10 h-10 flex items-center justify-center ${
                branding.logo_url ? "bg-transparent" : "bg-coral"
              }`}
            >
              {branding.logo_url && (
                <img
                  src={resolveImage(branding.logo_url)}
                  alt={branding.company_name || "Diyatour"}
                  className="w-7 h-7 object-contain"
                />
              )}
            </div>
            <div>
              <div className="font-logo font-extrabold text-[20px] tracking-tight">
                {branding.company_name || "DIYATOUR"}
              </div>
              <div className="font-menu text-[10px] tracking-[0.25em] text-white/40 uppercase">
                {branding.tagline || "CHINA · BUSINESS · TECH"}
              </div>
            </div>
          </div>
          <p className="text-white/60 max-w-md font-display text-[18px] leading-snug">
            {t.footer.tagline}
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-5">{t.footer.pages}</div>
          <ul className="space-y-2.5">
            {links.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-white/70 hover:text-coral transition-colors font-menu text-[15px]"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline mb-5">{t.footer.contact}</div>
          <ul className="space-y-3 font-mono text-[13px] text-white/70">
            <li>
              <a href={channels.telegram_url || "#"} className="hover:text-coral transition-colors">
                {channels.telegram_handle || "telegram"}
              </a>
            </li>
            <li>
              <a href={channels.whatsapp_url || "#"} className="hover:text-coral transition-colors">
                {channels.whatsapp_handle || "whatsapp"}
              </a>
            </li>
            <li>WeChat: {channels.wechat_handle || "diyatour"}</li>
            <li>
              <a
                href={`mailto:${channels.email || ""}`}
                className="hover:text-coral transition-colors"
              >
                {channels.email || "hello@diyatour.com"}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-white/35 font-mono text-[11px] tracking-wider uppercase">
          <div>
            © {new Date().getFullYear()} {branding.company_name || "Diyatour"}. {t.footer.rights}.
          </div>
          <Link
            to="/admin/login"
            className="hover:text-coral transition-colors"
            data-testid="footer-admin-link"
          >
            admin
          </Link>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => (
  <div className="min-h-screen bg-midnight text-white">
    <Header />
    <main>{children}</main>
    <Footer />
  </div>
);
